// src/content/marks.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  Four marks for managers, from his daily disciplines, so an owner
//               can recognise the person who runs the floor. `audience` says
//               who each mark is for and the give sheet shows only those.
//   2026-09-27  `cheer`, the line the salesperson reads when they open one.
//   2026-09-27  New. The marks a manager can give. Stored as `recognitions.mark`
//               by key, so adding one here is never a migration (0046).
//
// FOURTEEN MARKS, EACH ONE A BEHAVIOUR FROM JAZZ'S OWN SYSTEM.
//
// Not "great job", "rockstar" or "crushing it". Recognition has to mean
// something to the giver and the receiver (Gallup), and a mark that names what
// the manager actually saw is the difference between being noticed and being
// patted on the head. "Trade done right" tells a salesperson exactly which
// thing to keep doing. "Great job" tells them their manager was in a good mood.
//
// TWO AUDIENCES. Ten for the floor, one per behaviour from the steps. Four for
// managers, one per discipline from his management system that leaves a trace
// here: the morning meeting, a sign off that was watched, the one on one and
// recognising the floor. Recognition points down every level: a manager
// recognises the floor, an owner recognises the manager.
//
// THE STEP NUMBERS ARE THE ACADEMY'S, which match both of his 2026 manuals.
// The marketing site listed steps 4 to 8 in a different order until
// 2026-09-27; if a number here ever disagrees with src/content/steps.json,
// this file is wrong. tests/marks.test.mjs checks it.
//
// `saw` IS FOR THE MANAGER choosing the mark. `cheer` IS FOR THE SALESPERSON
// receiving it: one line on why that behaviour matters, paraphrased from the
// step's own purpose in steps.json, so the reason is canon's rather than ours.
// Reasons are ours to give under the charter; lines to say to a customer are
// not, and neither field is one.
//
// THESE ARE NAMES FOR RECOGNITION, NOT WORD TRACKS. Nothing here is a line a
// salesperson says to a customer. They are the label on the thing a manager
// hands a salesperson, which is product copy in the same way a button is.
//
// THE ICONS ARE STAND-INS. Each mark currently draws a plain line icon inside
// a medallion. docs/MARKS.md is the brief for a designer to draw a proper set,
// closer to a lapel pin or a challenge coin than to a sticker, and when those
// exist they replace `icon` here with no other code change.
//
// Voice rules: no Oxford commas. No em dashes. Real apostrophes.

export const MARKS = [
  {
    key: 'kept',
    name: 'Kept it',
    saw: 'Did the thing they committed to on their next customer.',
    step: null,
    cheer: 'You did what you said you would. That is the habit everything else is built on.',
    audience: 'floor',
    icon: 'Target',
  },
  {
    key: 'greet',
    name: 'Clean greet',
    saw: 'Met the customer the way the step is taught, and it sounded like a person.',
    step: 1,
    cheer: 'A good first impression lowers resistance for the whole visit, and yours did.',
    audience: 'floor',
    icon: 'Hand',
  },
  {
    key: 'discovery',
    name: 'Real discovery',
    saw: 'Found out what mattered before showing a single unit.',
    step: 2,
    cheer: 'Knowing what matters before choosing a unit makes every step after it easier.',
    audience: 'floor',
    icon: 'MessageCircleQuestion',
  },
  {
    key: 'intro',
    name: 'Brought the manager in',
    saw: 'Made the manager introduction on the up, early.',
    step: 3,
    cheer: 'Leadership met early builds the confidence that carries the rest of the deal.',
    audience: 'floor',
    icon: 'Users',
  },
  {
    key: 'benefit',
    name: 'Sold the benefit',
    saw: 'Took a feature through what it does to what it means for this customer.',
    step: 4,
    cheer: 'Product knowledge turned into value this customer could see. That is the job.',
    audience: 'floor',
    icon: 'Lightbulb',
  },
  {
    key: 'drive',
    name: 'The drive did it',
    saw: 'Ran a demonstration that built ownership rather than a lap round the block.',
    step: 5,
    cheer: 'The drive reinforced what matters most to them, which is what a drive is for.',
    audience: 'floor',
    icon: 'Gauge',
  },
  {
    key: 'service',
    name: 'Walked them to service',
    saw: 'Introduced service and parts before the numbers, not after.',
    step: 7,
    cheer: 'Showing how the store supports ownership is a reason to buy here and not somewhere else.',
    audience: 'floor',
    icon: 'Wrench',
  },
  {
    key: 'trade',
    name: 'Trade done right',
    saw: 'Walked the trade properly and got the information honestly.',
    step: 8,
    cheer: 'Accurate information with the customer involved is how trust survives the trade.',
    audience: 'floor',
    icon: 'Scale',
  },
  {
    key: 'callback',
    name: 'The callback worked',
    saw: 'Followed up with somebody who walked, and they came back in.',
    step: 12,
    cheer: 'Somebody who walked came back because you called. Do it again today.',
    audience: 'floor',
    icon: 'PhoneIncoming',
  },
  {
    key: 'teammate',
    name: 'Helped a teammate',
    saw: 'Showed somebody else on the floor how they run a step.',
    step: null,
    cheer: 'You made somebody else better. That is how a whole floor gets good.',
    audience: 'floor',
    icon: 'HeartHandshake',
  },
  {
    key: 'meeting',
    name: 'Ran the morning meeting',
    saw: 'Prepared the agenda before the shift and ran it on one skill focus.',
    cheer: 'A floor that starts the day on one skill sells differently by lunch.',
    step: null,
    audience: 'manager',
    icon: 'Sunrise',
  },
  {
    key: 'watched',
    name: 'Signed off by watching',
    saw: 'Stood next to somebody on a real customer before approving their step.',
    cheer: 'A sign off that means something is the one part of this nobody can automate.',
    step: null,
    audience: 'manager',
    icon: 'Eye',
  },
  {
    key: 'oneonone',
    name: 'Sat down with their people',
    saw: 'Held one on ones this week and came back to look when they said they would.',
    cheer: 'Coaching that comes back to check is the difference between a talk and a change.',
    step: null,
    audience: 'manager',
    icon: 'MessageSquare',
  },
  {
    key: 'noticed',
    name: 'Noticed the floor',
    saw: 'Recognised people on their floor for what they actually did this week.',
    cheer: 'People who are noticed every week stay, and your floor knows what you are watching for.',
    step: null,
    audience: 'manager',
    icon: 'Megaphone',
  },
]

export const markByKey = (key) => MARKS.find((m) => m.key === key) || null

// The marks that fit a person, by their job. Anybody who is not a manager or
// an owner gets the floor's ten, which is the safe default for an unknown role.
export const marksFor = (role) =>
  MARKS.filter((m) => m.audience === (role === 'manager' || role === 'owner' ? 'manager' : 'floor'))
